/**
 * Country Picker Components
 *
 * Shared country selection used in:
 * - Location filter chip (countries[])
 * - Filters modal → Home Country (home_countries[])
 *
 * Pattern: inline chips + "Add" button → opens shared modal picker.
 * Clicking a country in the modal immediately adds it and closes.
 */

import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';

/**
 * Inline country chips component.
 * Renders selected countries as chips with an "+ Add" button.
 *
 * Usage:
 *   x-data="countryChips('countries[]')"
 *   x-init="init(countriesData, allCountriesMap, suggestionsData)"
 */
export function countryChips(inputName) {
    return {
        inputName,
        countries: [],      // [{code, name}]
        allCountries: {},   // {CODE: 'Name', ...}
        suggestions: [],    // [{code, name}]

        init(selected, all, suggestions) {
            this.allCountries = all || {};
            this.countries = (selected || []).map(item => {
                if (typeof item === 'object' && item.code) return item;
                const code = String(item).toUpperCase();
                return { code, name: this.allCountries[code] || code };
            });
            this.suggestions = (suggestions || []).map(item => {
                if (typeof item === 'object' && item.code) return item;
                const code = String(item).toUpperCase();
                return { code, name: this.allCountries[code] || code };
            });
        },

        get filteredSuggestions() {
            const selectedCodes = new Set(this.countries.map(c => c.code));
            return this.suggestions.filter(s => !selectedCodes.has(s.code)).slice(0, 3);
        },

        addCountry(code) {
            if (!this.countries.some(c => c.code === code)) {
                this.countries.push({ code, name: this.allCountries[code] || code });
            }
        },

        removeCountry(code) {
            this.countries = this.countries.filter(c => c.code !== code);
        },

        getFlagUrl(code) {
            return '/images/circle-flags/flags/' + code.toLowerCase() + '.svg';
        },

        openModal() {
            const selectedCodes = this.countries.map(c => c.code);
            const self = this;
            this.$dispatch('open-country-picker', {
                allCountries: this.allCountries,
                selectedCodes,
                onSelect(code) {
                    const exists = self.countries.some(c => c.code === code);
                    if (!exists) {
                        self.countries.push({
                            code,
                            name: self.allCountries[code] || code
                        });
                    }
                }
            });
        }
    };
}

/**
 * Country picker modal component.
 * Opened via `@open-country-picker.window`.
 * Clicking a country immediately adds it and closes the modal.
 */
export function countryPickerModal() {
    return {
        isOpen: false,
        searchText: '',
        allCountries: [],   // [{code, name}] sorted
        selectedCodes: [],
        onSelectCallback: null,

        init() {
            window.addEventListener('open-country-picker', (e) => {
                this.open(e.detail);
            });
        },

        open(detail) {
            const map = detail.allCountries || {};
            this.allCountries = Object.entries(map)
                .map(([code, name]) => ({ code, name }))
                .sort((a, b) => a.name.localeCompare(b.name));

            this.selectedCodes = [...(detail.selectedCodes || [])];
            this.onSelectCallback = detail.onSelect || null;
            this.searchText = '';
            this.isOpen = true;
            document.body.classList.add('picker-modal-open');
            lockBodyScroll();
            this.$nextTick(() => {
                window.LucideIcons?.initialize(this.$el);
                const input = this.$refs.countrySearch;
                if (input) input.focus();
            });
        },

        close() {
            this.isOpen = false;
            document.body.classList.remove('picker-modal-open');
            unlockBodyScroll();
        },

        destroy() {
            document.body.classList.remove('picker-modal-open');
        },

        get filteredCountries() {
            const term = this.searchText.toLowerCase().trim();
            return this.allCountries.filter(c => {
                if (this.selectedCodes.includes(c.code)) return false;
                if (!term) return true;
                return c.name.toLowerCase().includes(term);
            });
        },

        selectCountry(code) {
            if (this.onSelectCallback) {
                this.onSelectCallback(code);
            }
            this.close();
        }
    };
}
